import { IFinding, IValidationInput, IValidationOutput, IValidator } from "../types";
import { OVD_BOUNDS, OVD_ROOT_KEYS, OVD_SCHEMA_IDS, OVD_VERSION_RE } from "./consts";
import { PositionFormatEnum, ValuesSourceEnum } from "open-vessel-definition";
import {
  isContainerLength,
  isFiniteNumber,
  isForeAft,
  isIsoBayKey,
  isPlainObject,
  isRowKey,
  isTierKey,
  isYesNo,
  tierWithinLevel,
} from "./patterns";

import { createValidationOutput } from "../helpers/createValidationOutput";

const name = "rootStructureValidator";
const okTranslationKey = "validators:structure.rootStructure.ok";
const errorTranslationKey = "validators:structure.rootStructure.error";

export type TRootStructureCode =
  | "missing"
  | "wrongType"
  | "badEnum"
  | "badPattern"
  | "badParity"
  | "outOfBounds"
  | "duplicate"
  /** Two fields contradict each other, e.g. a min above its max */
  | "inconsistent";

export interface IRootStructureFinding extends IFinding {
  code: TRootStructureCode;
  /** Absolute path from the root, e.g. `shipData.masterCGs.aboveTcgs.7` */
  path: string;
}

export interface IRootStructureOutput {
  findings: IRootStructureFinding[];
}

/**
 * The shape of the root of the file: the nine root keys, `$schemaId`, `version`,
 * `sizeSummary`, and the required parts of `shipData` (`masterCGs` and the CG options
 * among them), with their patterns, enums and bounds. It never throws, whatever the JSON.
 *
 * Bays, slots, lids and vessel parts have their own structure validators.
 */
export const rootStructureValidator: IValidator<IRootStructureOutput> = {
  name,
  okTranslationKey,
  errorTranslationKey,
  fn: rootStructureValidatorFn,
  severity: "error",
  stage: "structure",
};

export function rootStructureValidatorFn({
  data: { json },
}: IValidationInput): IValidationOutput<IRootStructureOutput> {
  const findings: IRootStructureFinding[] = [];
  const flag = (code: TRootStructureCode, path: string) => findings.push({ code, path });

  const root: Record<string, unknown> = isPlainObject(json) ? json : {};
  if (!isPlainObject(json)) flag("wrongType", "");

  OVD_ROOT_KEYS.forEach((key) => {
    if (root[key] === undefined) flag("missing", key);
  });

  if (root["$schema"] !== undefined && typeof root["$schema"] !== "string")
    flag("wrongType", "$schema");
  if (root["$schemaId"] !== undefined && !OVD_SCHEMA_IDS.includes(root["$schemaId"] as string))
    flag("badEnum", "$schemaId");

  if (root["version"] !== undefined) {
    if (typeof root["version"] !== "string") flag("wrongType", "version");
    else if (!OVD_VERSION_RE.test(root["version"])) flag("badPattern", "version");
  }

  (["baysData", "lidData", "vesselPartsData"] as const).forEach((key) => {
    if (root[key] !== undefined && !Array.isArray(root[key])) flag("wrongType", key);
  });

  (["sizeSummary", "shipData", "positionLabels"] as const).forEach((key) => {
    if (root[key] !== undefined && !isPlainObject(root[key])) flag("wrongType", key);
  });

  if (isPlainObject(root["sizeSummary"])) checkSizeSummary(root["sizeSummary"], flag);
  if (isPlainObject(root["shipData"])) checkShipData(root["shipData"], flag);

  return createValidationOutput(rootStructureValidator, findings, { findings });
}

type TFlag = (code: TRootStructureCode, path: string) => void;

function checkSizeSummary(ss: Record<string, unknown>, flag: TFlag): void {
  const p = "sizeSummary";

  if (ss["isoBays"] === undefined) flag("missing", `${p}.isoBays`);
  else if (!isFiniteNumber(ss["isoBays"])) flag("wrongType", `${p}.isoBays`);
  else {
    if (ss["isoBays"] % 2 !== 1) flag("badParity", `${p}.isoBays`);
    if (ss["isoBays"] < OVD_BOUNDS.BAY_MIN || ss["isoBays"] >= OVD_BOUNDS.BAY_MAX_EXCLUSIVE)
      flag("outOfBounds", `${p}.isoBays`);
  }

  if (ss["centerLineRow"] === undefined) flag("missing", `${p}.centerLineRow`);
  else if (!isYesNo(ss["centerLineRow"])) flag("badEnum", `${p}.centerLineRow`);

  if (ss["maxRow"] !== undefined) {
    if (!isFiniteNumber(ss["maxRow"])) flag("wrongType", `${p}.maxRow`);
    else if (ss["maxRow"] < OVD_BOUNDS.ROW_MIN || ss["maxRow"] > OVD_BOUNDS.ROW_MAX)
      flag("outOfBounds", `${p}.maxRow`);
  }

  const tiers: Array<[string, 1 | 2]> = [
    ["minAboveTier", 1],
    ["maxAboveTier", 1],
    ["minBelowTier", 2],
    ["maxBelowTier", 2],
  ];

  tiers.forEach(([key, level]) => {
    const v = ss[key];
    if (v === undefined) return;
    if (!isFiniteNumber(v)) return flag("wrongType", `${p}.${key}`);
    if (v % 2 !== 0) flag("badParity", `${p}.${key}`);
    if (!tierWithinLevel(v, level)) flag("outOfBounds", `${p}.${key}`);
  });

  (["Above", "Below"] as const).forEach((lv) => {
    const min = ss[`min${lv}Tier`];
    const max = ss[`max${lv}Tier`];
    if (isFiniteNumber(min) && isFiniteNumber(max) && min > max)
      flag("inconsistent", `${p}.min${lv}Tier`);
  });
}

function checkShipData(sd: Record<string, unknown>, flag: TFlag): void {
  const p = "shipData";

  if (sd["shipClass"] === undefined) flag("missing", `${p}.shipClass`);
  else if (typeof sd["shipClass"] !== "string") flag("wrongType", `${p}.shipClass`);

  if (sd["positionFormat"] === undefined) flag("missing", `${p}.positionFormat`);
  else if (!Object.values(PositionFormatEnum).includes(sd["positionFormat"] as PositionFormatEnum))
    flag("badEnum", `${p}.positionFormat`);

  (["lcgOptions", "vcgOptions", "tcgOptions"] as const).forEach((key) => {
    const opts = sd[key];
    if (opts === undefined) return flag("missing", `${p}.${key}`);
    if (!isPlainObject(opts)) return flag("wrongType", `${p}.${key}`);

    if (opts["values"] === undefined) flag("missing", `${p}.${key}.values`);
    else if (
      opts["values"] !== ValuesSourceEnum.ESTIMATED &&
      opts["values"] !== ValuesSourceEnum.KNOWN
    )
      flag("badEnum", `${p}.${key}.values`);

    if (key === "lcgOptions") {
      if (opts["lpp"] === undefined) flag("missing", `${p}.${key}.lpp`);
      else if (!isFiniteNumber(opts["lpp"])) flag("wrongType", `${p}.${key}.lpp`);
    }

    if (
      key === "vcgOptions" &&
      opts["heightFactor"] !== undefined &&
      !isFiniteNumber(opts["heightFactor"])
    )
      flag("wrongType", `${p}.${key}.heightFactor`);
  });

  const lengths = sd["containersLengths"];
  if (lengths === undefined) flag("missing", `${p}.containersLengths`);
  else if (!Array.isArray(lengths)) flag("wrongType", `${p}.containersLengths`);
  else {
    const seen = new Set<number>();
    lengths.forEach((len, i) => {
      if (!isContainerLength(len)) return flag("badEnum", `${p}.containersLengths.${i}`);
      if (seen.has(len)) flag("duplicate", `${p}.containersLengths.${i}`);
      seen.add(len);
    });
  }

  if (sd["bay40sLocation"] !== undefined && !isForeAft(sd["bay40sLocation"]))
    flag("badEnum", `${p}.bay40sLocation`);

  (["loa", "sternToAftPp", "yearBuilt"] as const).forEach((key) => {
    if (sd[key] !== undefined && !isFiniteNumber(sd[key])) flag("wrongType", `${p}.${key}`);
  });

  const fa = sd["featuresAllowed"];
  if (fa !== undefined) {
    if (!isPlainObject(fa)) flag("wrongType", `${p}.featuresAllowed`);
    else
      (["slotCoolStowProhibited", "slotHazardousProhibited", "slotConeRequired"] as const).forEach(
        (key) => {
          if (typeof fa[key] !== "boolean") flag("wrongType", `${p}.featuresAllowed.${key}`);
        },
      );
  }

  const masterCGs = sd["masterCGs"];
  if (masterCGs === undefined) return flag("missing", `${p}.masterCGs`);
  if (!isPlainObject(masterCGs)) return flag("wrongType", `${p}.masterCGs`);

  const tables: Array<[string, (k: unknown) => boolean]> = [
    ["aboveTcgs", isRowKey],
    ["belowTcgs", isRowKey],
    ["bottomBases", isTierKey],
  ];

  tables.forEach(([key, isKey]) => {
    const table = masterCGs[key];
    if (table === undefined) return flag("missing", `${p}.masterCGs.${key}`);
    if (!isPlainObject(table)) return flag("wrongType", `${p}.masterCGs.${key}`);

    Object.keys(table).forEach((k) => {
      if (!isKey(k)) flag("badPattern", `${p}.masterCGs.${key}.${k}`);
      if (!isFiniteNumber(table[k])) flag("wrongType", `${p}.masterCGs.${key}.${k}`);
    });
  });

  const offsets = masterCGs["offsetBottomBases"];
  if (offsets === undefined) return;
  if (!Array.isArray(offsets)) return flag("wrongType", `${p}.masterCGs.offsetBottomBases`);

  offsets.forEach((entry, i) => {
    const ep = `${p}.masterCGs.offsetBottomBases.${i}`;
    if (!isPlainObject(entry)) return flag("wrongType", ep);

    if (!Array.isArray(entry["bays"])) flag("wrongType", `${ep}.bays`);
    else
      (entry["bays"] as unknown[]).forEach((b, j) => {
        if (!isIsoBayKey(b)) flag("badPattern", `${ep}.bays.${j}`);
      });

    if (entry["level"] !== 1 && entry["level"] !== 2) flag("badEnum", `${ep}.level`);
    if (!isFiniteNumber(entry["offset"])) flag("wrongType", `${ep}.offset`);
  });
}
